// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import qs from 'query-string';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { type ModalProps } from '@/stores/use-modal-store.ts';
import React, { useState } from 'react';
import { S3_ENDPOINT_BUCKET } from '@/config';
import type { File as S3File } from '@/models';
import { UploadFilesS3PresignedUrl } from '@/components/files/upload-files-s3-presigned-url';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const formSchema = z.object({
  fileUrl: z.string().min(1, {
    message: 'Attachment is required.',
  }),
});

export const MessageFileModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const { t } = useTranslation();
  const [fileUrl, setFileUrl] = useState('');
  const { apiUrl, query } = data;
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fileUrl: '',
    },
  });

  const handleClose = () => {
    form.reset();
    setFileUrl('');
    setHasSubmitted(false);
    onClose();
  };

  const isLoading = form.formState.isSubmitting;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (hasSubmitted) return;
    setHasSubmitted(true);

    values = { ...values, fileUrl: fileUrl };
    try {
      const url = qs.stringifyUrl({
        url: apiUrl || '',
        query,
      });
      await fetch(url, {
        method: 'POST',
        body: JSON.stringify({
          ...values,
          content: values.fileUrl,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      handleClose();
    } catch {
      // TODO: translate
      toast.error(`Cant upload file.`);
      setHasSubmitted(false);
    }
  };

  const onSubmitImage = async (savedFiles: S3File[]) => {
    const fileUrl = S3_ENDPOINT_BUCKET + '/' + savedFiles[0].fileName;
    setFileUrl(fileUrl);
    form.setValue('fileUrl', fileUrl);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="text-center text-2xl font-bold">
                {t('MessageFileModal.title')}
              </div>
              <div className="text-center">
                {t('MessageFileModal.description')}
              </div>
            </ModalHeader>
            <ModalBody>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-2"
                >
                  <div className="space-y-2 px-6">
                    <div className="flex items-center justify-center text-center">
                      <FormField
                        control={form.control}
                        name="fileUrl"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <UploadFilesS3PresignedUrl
                                onUploadSuccess={() => {
                                  // TODO: translate
                                  toast.success('Files uploaded');
                                }}
                                onFilesSaved={onSubmitImage}
                                folderName="messageFile"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <ModalFooter>
                    <Button
                      color="primary"
                      variant="primary"
                      disabled={isLoading}
                      type="submit"
                    >
                      {t('MessageFileModal.send')}
                    </Button>
                  </ModalFooter>
                </form>
              </Form>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
